import { useMemo } from "react";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Trash2, Sparkles, ThumbsUp, ThumbsDown } from "lucide-react";
import { HTMLRenderer } from "@/modern/components/ui/html-renderer";
import { cn } from "@/modern/utils";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { useFileInsights } from "@/react/hooks/entityHooks/useFileInsights";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { INSIGHT_TYPES } from "@/bryntum-lib/models/Insights";
import { Insights } from "@/bryntum-lib/models/Insights";
import { UserAvatar, BoardUser } from "@/modern/components/user-avatar";
import { User } from "@/bryntum-lib/models/User";

export interface Finding {
  id: string;
  category: string;
  description: string;
  positiveFeedbackUsers: BoardUser[];
  negativeFeedbackUsers: BoardUser[];
  insightType: string;
}

const getCategoryFromInsightType = (insightType: string): string => {
  const insightTypeConfig = Object.values(INSIGHT_TYPES).find((type) => type.value === insightType);
  return insightTypeConfig?.label || insightType;
};

export const KeyFindingsTable = ({ compact = false }: { compact?: boolean }) => {
  const { file, project } = useFileDetailModal();
  const { getInsights, deleteInsight } = useFileInsights();

  const { version: insightsVersion } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });

  const insights = useMemo(() => {
    if (!file?.id) return [];
    return getInsights(file?.id);
  }, [file?.id, getInsights, insightsVersion]);

  const findings = useMemo<Finding[]>(() => {
    return insights.map((insight: Insights) => {
      const positiveUsers: BoardUser[] = [];
      if (insight.positiveFeedbackUsers && project?.boardUsersStore) {
        insight.positiveFeedbackUsers.forEach((userId: number) => {
          const user = project.boardUsersStore.getById(userId) as User | undefined;
          if (user) {
            positiveUsers.push({
              id: user.id,
              name: user.name || "Unknown",
              profileImageURL: user.profileImageURL,
              archived: user.archived || false,
            });
          }
        });
      }

      const negativeUsers: BoardUser[] = [];
      if (insight.negativeFeedbackUsers && project?.boardUsersStore) {
        insight.negativeFeedbackUsers.forEach((userId: number) => {
          const user = project.boardUsersStore.getById(userId) as User | undefined;
          if (user) {
            negativeUsers.push({
              id: user.id,
              name: user.name || "Unknown",
              profileImageURL: user.profileImageURL,
              archived: user.archived || false,
            });
          }
        });
      }

      return {
        id: String(insight.id),
        category: getCategoryFromInsightType(insight.insightType),
        description: insight.content || "",
        positiveFeedbackUsers: positiveUsers,
        negativeFeedbackUsers: negativeUsers,
        insightType: insight.insightType || "",
      };
    });
  }, [insights, project]);

  const handleRemove = (id: string) => {
    deleteInsight(id);
  };

  if (findings.length === 0) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center overflow-hidden",
          compact ? "py-6" : "h-full p-6",
        )}
      >
        <div
          className={cn(
            "mx-auto flex w-full max-w-md flex-col items-center text-center",
            compact
              ? "gap-3 rounded-lg border border-dashed border-border/50 bg-muted/20 px-4 py-6"
              : "gap-4 rounded-xl border border-dashed border-border/50 bg-muted/20 px-6 py-8",
          )}
        >
          <div
            className={cn(
              "mx-auto flex items-center justify-center rounded-full bg-primary/10 text-primary ring-4 ring-primary/5",
              compact ? "h-10 w-10" : "h-12 w-12",
            )}
          >
            <Sparkles className={compact ? "size-5" : "size-6"} />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-normal text-foreground">No findings recorded</h3>
            <p className="max-w-sm text-sm font-normal leading-relaxed text-muted-foreground">
              Analyze this file to generate key findings and insights.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(compact ? "space-y-2 overflow-visible p-0.5 pb-1" : "space-y-4 overflow-y-auto p-4")}>
      {findings.map((finding) => {
        const hasFeedback =
          finding.positiveFeedbackUsers.length > 0 || finding.negativeFeedbackUsers.length > 0;

        if (compact) {
          return (
            <div
              key={finding.id}
              className="group rounded-md border border-border/60 bg-muted/40 p-3 transition-colors hover:bg-muted/70"
            >
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="h-5 px-1.5 text-[0.6875rem] font-normal">
                      {finding.category}
                    </Badge>
                  </div>
                  <div className="text-sm font-normal leading-snug text-foreground [&_*]:text-sm [&_*]:leading-snug">
                    <HTMLRenderer content={finding.description} />
                  </div>
                  {hasFeedback && (
                    <div className="flex flex-wrap items-center gap-3 pt-0.5">
                      {finding.positiveFeedbackUsers.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <ThumbsUp className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                          <div className="flex items-center -space-x-1.5">
                            {finding.positiveFeedbackUsers.map((user) => (
                              <UserAvatar key={user.id} user={user} size="small" showTooltip />
                            ))}
                          </div>
                        </div>
                      )}
                      {finding.negativeFeedbackUsers.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          <ThumbsDown className="size-3 shrink-0 text-muted-foreground" aria-hidden />
                          <div className="flex items-center -space-x-1.5">
                            {finding.negativeFeedbackUsers.map((user) => (
                              <UserAvatar key={user.id} user={user} size="small" showTooltip />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                  onClick={() => handleRemove(finding.id)}
                  aria-label={`Remove finding ${finding.id}`}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div
            key={finding.id}
            className="overflow-hidden rounded-lg border border-border/50 bg-card transition-all hover:bg-muted/20"
          >
            <div className="space-y-4 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant="secondary" className="text-xs font-medium">
                    {finding.category}
                  </Badge>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => handleRemove(finding.id)}
                  aria-label={`Remove finding ${finding.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <HTMLRenderer content={finding.description} />

              {hasFeedback && (
                <div className="space-y-3 border-t border-border/50 pt-2">
                  {finding.positiveFeedbackUsers.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                        <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                        <span>Positive Feedback ({finding.positiveFeedbackUsers.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-2 pl-5">
                        {finding.positiveFeedbackUsers.map((user) => (
                          <UserAvatar key={user.id} user={user} size="small" showTooltip />
                        ))}
                      </div>
                    </div>
                  )}
                  {finding.negativeFeedbackUsers.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                        <ThumbsDown className="h-3.5 w-3.5 text-red-600" />
                        <span>Negative Feedback ({finding.negativeFeedbackUsers.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-2 pl-5">
                        {finding.negativeFeedbackUsers.map((user) => (
                          <UserAvatar key={user.id} user={user} size="small" showTooltip />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
